import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import type {
	FilterConfig,
	FilterRule,
	ListBuilderConfiguration,
	ListConfiguration,
} from '@supply-mover-2-0/core';
import {
	CustomFieldQueryService,
	FilterRegistry,
	getValidFiltersForObjects,
} from '@supply-mover-2-0/core';
import type { CustomFieldEntityType } from '@supply-mover-2-0/shared';
import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Icon } from '../../../components/base/Icon';
import { Checkbox } from '../../../components/forms/Checkbox';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { DraggableList } from '../shared/DraggableList';
import { FilterRuleCard } from './FilterRuleCard';

export interface FiltersTabState {
	hasPendingChanges: boolean;
}

/**
 * Snapshot of the filter-tab edits that ColumnEditorLayout's saveAll will
 * serialize into the saved-search update payload. activeFilters/listOverrides
 * may be null to mean "inherit", or an array/object to mean "override".
 */
export interface FiltersTabPendingChanges {
	activeFilters: Array<string> | null;
	filterRules: Record<string, FilterRule> | null;
	listOverrides: Record<string, unknown> | null;
}

export interface FiltersTabHandle {
	/**
	 * Return the working state for the currently selected list, even if
	 * unsaved. ColumnEditorLayout calls this in its saveAll path so changes
	 * across both tabs persist in one update.
	 */
	getPendingChanges: () => FiltersTabPendingChanges | null;
	/**
	 * Reset the working state to the saved-search's persisted values. Called
	 * when the user discards changes or after a successful save.
	 */
	reset: () => void;
}

export interface FiltersTabContentProps {
	/**
	 * The merged list config (base list + saved-search overrides applied).
	 * Provides the starting point for activeFilters and filterRules edits, plus
	 * the configuration.objects we use to filter the available registry.
	 */
	listConfig: ListConfiguration;
	configuration: ListBuilderConfiguration | undefined;
	/** Raw saved-search overrides — null fields = inherit. Used to seed working state. */
	savedSearchOverrides: {
		activeFilters: Array<string> | null;
		filterRules: Record<string, FilterRule> | null;
		listOverrides: Record<string, unknown> | null;
	} | null;
	isReadOnly: boolean;
	onStateChange: (state: FiltersTabState) => void;
}

/**
 * Filters tab for the List Configuration modal. Three sections vertically:
 *   1. List behavior — showFiltersByDefault checkbox (persists to
 *      listOverrides JSON).
 *   2. Active filters — consolidated list of FilterRuleCards. Each card is
 *      a saved-search-overrideable filter with its defaultValue editor, lock,
 *      label/placeholder overrides, and Advanced (permission conditions +
 *      additionalValues). Internal reorder via dnd-kit; cross-list drops
 *      from the Available pool come through ActiveFiltersDropTarget.
 *   3. Available filters — pool of filters not yet active. Native HTML5
 *      drag-and-drop drops items into Active at a chosen insertion point.
 *
 * Read-only when the user has selected a bundled (default) list on the
 * sidebar; they need to "Save As New" / "Copy List" to customize.
 */
export const FiltersTabContent = forwardRef<
	FiltersTabHandle,
	FiltersTabContentProps
>(
	(
		{
			listConfig,
			configuration,
			savedSearchOverrides,
			isReadOnly,
			onStateChange,
		},
		ref,
	) => {
		const { t } = useTranslation();
		const colors = useThemeColors();

		// Resolved active set: saved-search override wins, otherwise inherit
		// from the merged list config (which already includes per-list activeFilters
		// or falls back to configuration-level activeFilters).
		const resolvedActiveFilters = useMemo<Array<string>>(() => {
			if (savedSearchOverrides?.activeFilters != null) {
				return savedSearchOverrides.activeFilters;
			}
			if (listConfig.activeFilters != null) {
				return listConfig.activeFilters;
			}
			return configuration?.filters?.activeFilters ?? [];
		}, [savedSearchOverrides, listConfig, configuration]);

		const resolvedFilterRules = useMemo<Record<string, FilterRule>>(() => {
			if (savedSearchOverrides?.filterRules != null) {
				return savedSearchOverrides.filterRules;
			}
			return listConfig.filterRules ?? {};
		}, [savedSearchOverrides, listConfig]);

		const resolvedListOverrides = useMemo<Record<string, unknown>>(() => {
			if (savedSearchOverrides?.listOverrides != null) {
				return savedSearchOverrides.listOverrides;
			}
			return {};
		}, [savedSearchOverrides]);

		// Working state — initialized from resolved, mutated by user.
		const [activeFilters, setActiveFilters] = useState<Array<string>>(
			resolvedActiveFilters,
		);
		const [filterRules, setFilterRules] =
			useState<Record<string, FilterRule>>(resolvedFilterRules);
		const [listOverrides, setListOverrides] = useState<Record<string, unknown>>(
			resolvedListOverrides,
		);
		const [isDirty, setIsDirty] = useState(false);

		// Re-seed working state only when the user actually selects a different
		// list in the sidebar. listConfig.id is the per-list identifier (saved
		// search id or bundled list id). Without this guard we'd reset every
		// parent render because workingListConfig is rebuilt as a new object
		// each render and resolvedActiveFilters etc. lose reference equality —
		// wiping any in-progress edits the user made.
		const previousListIdRef = useRef<string | null>(null);
		useEffect(() => {
			const currentId = listConfig.id;
			if (previousListIdRef.current === currentId) {
				return;
			}
			previousListIdRef.current = currentId;
			setActiveFilters(resolvedActiveFilters);
			setFilterRules(resolvedFilterRules);
			setListOverrides(resolvedListOverrides);
			setIsDirty(false);
		}, [
			listConfig.id,
			resolvedActiveFilters,
			resolvedFilterRules,
			resolvedListOverrides,
		]);

		useEffect(() => {
			onStateChange({ hasPendingChanges: isDirty });
		}, [isDirty, onStateChange]);

		// Custom-field filters: fetched per object table in this list. Synthesized
		// server-side from CustomFieldDefinitionEntity rows so any tenant-defined
		// custom field (e.g. Pharmsource's sales_channel on order) shows up as an
		// available filter automatically — no code change per filter.
		const objectTables = useMemo(
			() => (configuration?.objects ?? []).map((o) => o.objectTable),
			[configuration?.objects],
		);

		const [customFieldConfigs, setCustomFieldConfigs] = useState<
			Record<string, FilterConfig>
		>({});

		useEffect(() => {
			let cancelled = false;
			void (async (): Promise<void> => {
				const merged: Record<string, FilterConfig> = {};
				for (const objectTable of objectTables) {
					const result = await CustomFieldQueryService.getFilterConfigs(
						objectTable as CustomFieldEntityType,
					);
					if (result.success) {
						Object.assign(merged, result.data);
					}
				}
				if (!cancelled) {
					setCustomFieldConfigs(merged);
				}
			})();
			return () => {
				cancelled = true;
			};
		}, [objectTables.join(',')]);

		// Available filters: anything in FILTER_REGISTRY valid for this list's
		// objects PLUS synthesized custom-field configs, minus what's already
		// active. Filters not in either source still render in the active list
		// (stale ids stay visible so the admin can remove them).
		const validFilterIds = useMemo(() => {
			return getValidFiltersForObjects(configuration?.objects ?? []);
		}, [configuration?.objects]);

		const availableFilters = useMemo(() => {
			const active = new Set(activeFilters);
			const fromRegistry = validFilterIds
				.filter((id) => !active.has(id))
				.map((id) => {
					const cfg = FilterRegistry.getConfig(id);
					return {
						id,
						label: cfg?.label ?? id,
						type: cfg?.type ?? 'unknown',
					};
				});
			const fromCustomFields = Object.entries(customFieldConfigs)
				.filter(([id]) => !active.has(id) && !validFilterIds.includes(id))
				.map(([id, cfg]) => ({
					id,
					label: cfg.label,
					type: cfg.type,
				}));
			return [...fromRegistry, ...fromCustomFields].sort((a, b) =>
				a.label.localeCompare(b.label),
			);
		}, [validFilterIds, activeFilters, customFieldConfigs]);

		const reorderActiveFilters = (newOrder: Array<string>): void => {
			if (isReadOnly) return;
			setActiveFilters(newOrder);
			markDirty();
		};

		const markDirty = (): void => {
			setIsDirty(true);
		};

		const addFilter = (filterId: string): void => {
			if (isReadOnly) return;
			setActiveFilters((prev) =>
				prev.includes(filterId) ? prev : [...prev, filterId],
			);
			markDirty();
		};

		/**
		 * Insert a filter at a specific index in the active list. Called by
		 * ActiveFiltersDropTarget when a native HTML5 drop lands on a slot.
		 * Idempotent — if the filter is already active and the user re-drops
		 * it to a different slot, we move it.
		 */
		const addFilterAtPosition = (
			filterId: string,
			targetIndex: number,
		): void => {
			if (isReadOnly) return;
			setActiveFilters((prev) => {
				const without = prev.filter((id) => id !== filterId);
				const clampedIndex = Math.max(0, Math.min(targetIndex, without.length));
				const next = [...without];
				next.splice(clampedIndex, 0, filterId);
				return next;
			});
			markDirty();
		};

		const removeFilter = (filterId: string): void => {
			if (isReadOnly) return;
			setActiveFilters((prev) => prev.filter((id) => id !== filterId));
			// Also drop the rule for the removed filter so we don't carry stale config
			setFilterRules((prev) => {
				if (!(filterId in prev)) {
					return prev;
				}
				const next = { ...prev };
				delete next[filterId];
				return next;
			});
			markDirty();
		};

		const updateRule = (
			filterId: string,
			rule: FilterRule | undefined,
		): void => {
			if (isReadOnly) return;
			setFilterRules((prev) => {
				const next = { ...prev };
				if (rule == null) {
					delete next[filterId];
				} else {
					next[filterId] = rule;
				}
				return next;
			});
			markDirty();
		};

		const setShowFiltersByDefault = (value: boolean): void => {
			if (isReadOnly) return;
			setListOverrides((prev) => {
				const next = { ...prev, showFiltersByDefault: value };
				return next;
			});
			markDirty();
		};

		useImperativeHandle(ref, () => ({
			getPendingChanges: () => {
				if (!isDirty) return null;

				// Persist activeFilters as override when the user has touched anything.
				// Use null only when the working set matches the inherited set exactly
				// (i.e. the user could have just reverted — keep storage clean).
				const inheritedFiltersMatch =
					activeFilters.length === resolvedActiveFilters.length &&
					activeFilters.every((id, i) => id === resolvedActiveFilters[i]);
				const inheritedRulesMatch =
					JSON.stringify(filterRules) === JSON.stringify(resolvedFilterRules);
				const inheritedOverridesMatch =
					JSON.stringify(listOverrides) ===
					JSON.stringify(resolvedListOverrides);

				return {
					activeFilters: inheritedFiltersMatch ? null : activeFilters,
					filterRules:
						inheritedRulesMatch || Object.keys(filterRules).length === 0
							? null
							: filterRules,
					listOverrides:
						inheritedOverridesMatch || Object.keys(listOverrides).length === 0
							? null
							: listOverrides,
				};
			},
			reset: () => {
				setActiveFilters(resolvedActiveFilters);
				setFilterRules(resolvedFilterRules);
				setListOverrides(resolvedListOverrides);
				setIsDirty(false);
			},
		}));

		return (
			<div className="flex-1 overflow-y-auto p-4 space-y-4">
				{isReadOnly && (
					<div
						className={cn(
							'px-3 py-2 rounded border text-sm',
							colors.classes.borderDefault,
							'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
						)}
					>
						{t(
							'This is a read-only list. Use "Copy List" in the footer to create an editable copy.',
						)}
					</div>
				)}

				{/* List behavior — top of the tab so the global on/off toggle
				    is the first thing the admin sees. */}
				<section
					className={cn(
						'rounded-md border p-3',
						colors.classes.borderDefault,
						colors.classes.bgPrimary,
					)}
				>
					<header className="mb-2">
						<h3
							className={cn(
								'text-sm font-semibold',
								colors.classes.textPrimary,
							)}
						>
							{t('List behavior')}
						</h3>
					</header>
					<Checkbox
						checked={listOverrides.showFiltersByDefault !== false}
						onChange={(checked) => {
							setShowFiltersByDefault(checked);
						}}
						disabled={isReadOnly}
						label={t('Show filter bar expanded by default')}
						help={t(
							'When checked, the filter sidebar appears expanded the first time a user opens this list. They can still collapse it manually.',
						)}
					/>
				</section>

				{/* Active filters — single consolidated list. Each entry is an
				    expandable FilterRuleCard with a drag handle in its header.
				    Reorderable internally; accepts drops from the Available
				    filters list below, with positional insertion. */}
				<section
					className={cn(
						'rounded-md border p-3',
						colors.classes.borderDefault,
						colors.classes.bgPrimary,
					)}
				>
					<header className="flex items-center justify-between mb-2">
						<h3
							className={cn(
								'text-sm font-semibold',
								colors.classes.textPrimary,
							)}
						>
							{t(`Active filters (${String(activeFilters.length)})`)}
						</h3>
						<p className={cn('text-xs', colors.classes.textMuted)}>
							{t(
								'Drag the handle to reorder. Drag a filter from the available pool below into the slot you want.',
							)}
						</p>
					</header>

					{activeFilters.length === 0 ? (
						<EmptyActiveFiltersDropZone
							onDrop={(filterId) => {
								addFilter(filterId);
							}}
							disabled={isReadOnly}
						/>
					) : (
						<ActiveFiltersDropTarget
							onPositionalDrop={(filterId, index) => {
								addFilterAtPosition(filterId, index);
							}}
							disabled={isReadOnly}
						>
							<DraggableList
								data={activeFilters}
								keyExtractor={(filterId) => filterId}
								renderItem={(filterId, _index, isDraggingItem) => (
									<div data-active-filter-card="true">
										<FilterRuleCard
											filterId={filterId}
											rule={filterRules[filterId]}
											customFieldConfig={customFieldConfigs[filterId]}
											onChange={(next) => {
												updateRule(filterId, next);
											}}
											onRemove={() => {
												removeFilter(filterId);
											}}
											disabled={isReadOnly}
											showDragHandle={!isReadOnly}
											isDragging={isDraggingItem}
										/>
									</div>
								)}
								onReorder={reorderActiveFilters}
								allowInternalReordering={!isReadOnly}
								className="space-y-2"
							/>
						</ActiveFiltersDropTarget>
					)}
				</section>

				{/* Available filters — draggable into the Active section above */}
				<section
					className={cn(
						'rounded-md border p-3',
						colors.classes.borderDefault,
						colors.classes.bgPrimary,
					)}
				>
					<header className="mb-2">
						<h3
							className={cn(
								'text-sm font-semibold',
								colors.classes.textPrimary,
							)}
						>
							{t(`Available filters (${String(availableFilters.length)})`)}
						</h3>
						<p className={cn('text-xs', colors.classes.textMuted)}>
							{t('Drag a filter into the Active list to add it.')}
						</p>
					</header>

					{availableFilters.length === 0 ? (
						<p className={cn('text-xs py-2', colors.classes.textMuted)}>
							{t('No more filters available for this list’s objects.')}
						</p>
					) : (
						<div className="space-y-1 max-h-72 overflow-y-auto">
							{availableFilters.map((f) => (
								<AvailableFilterItem
									key={f.id}
									filter={f}
									disabled={isReadOnly}
								/>
							))}
						</div>
					)}
				</section>
			</div>
		);
	},
);

FiltersTabContent.displayName = 'FiltersTabContent';

interface AvailableFilterItemProps {
	filter: { id: string; label: string; type: string };
	disabled: boolean;
}

/**
 * One row in the Available filters list. Uses native HTML5 drag so it can be
 * dropped into the Active section's drop target (dnd-kit doesn't route drag
 * events between separate DndContexts, so we lean on the browser's built-in
 * drag mechanism for cross-list moves).
 */
function AvailableFilterItem({
	filter,
	disabled,
}: AvailableFilterItemProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const isCustomField = filter.id.startsWith('cf:');

	return (
		<div
			draggable={!disabled}
			onDragStart={(e) => {
				e.dataTransfer.setData('text/plain', filter.id);
				e.dataTransfer.effectAllowed = 'copy';
			}}
			className={cn(
				'flex items-center gap-2 px-2 py-1.5 rounded border select-none',
				colors.classes.borderDefault,
				colors.classes.bgPrimary,
				!disabled && 'cursor-grab hover:' + colors.classes.bgSecondary,
				disabled && 'opacity-50 cursor-not-allowed',
			)}
		>
			<Icon
				icon={faGripVertical}
				size="xs"
				className={cn('shrink-0', colors.classes.textMuted)}
			/>
			<span
				className={cn('text-sm flex-1 truncate', colors.classes.textPrimary)}
			>
				{t(filter.label)}
			</span>
			{isCustomField && (
				<span
					className={cn(
						'text-xs px-1.5 py-0.5 rounded shrink-0',
						'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
					)}
				>
					{t('custom')}
				</span>
			)}
			<span
				className={cn(
					'text-xs px-1.5 py-0.5 rounded shrink-0',
					colors.classes.bgSecondary,
					colors.classes.textSecondary,
				)}
			>
				{t(filter.type)}
			</span>
		</div>
	);
}

interface ActiveFiltersDropTargetProps {
	children: React.ReactNode;
	onPositionalDrop: (filterId: string, index: number) => void;
	disabled: boolean;
}

/**
 * Wraps the Active filters DraggableList so it can also receive native HTML5
 * drops from the Available filters list. dnd-kit handles internal reordering
 * of the active list with its own pointer sensor; cross-list drops come in
 * here. We compute the insertion index from the cursor's Y position relative
 * to each FilterRuleCard's midpoint, and render a horizontal line to preview
 * where the dropped filter will land.
 */
function ActiveFiltersDropTarget({
	children,
	onPositionalDrop,
	disabled,
}: ActiveFiltersDropTargetProps): React.ReactElement {
	const containerRef = useRef<HTMLDivElement>(null);
	const [hoverIndex, setHoverIndex] = useState<number | null>(null);
	const [indicatorTop, setIndicatorTop] = useState<number | null>(null);
	const colors = useThemeColors();

	const computeInsertion = (
		clientY: number,
	): { index: number; top: number } | null => {
		const container = containerRef.current;
		if (container == null) return null;
		const cards = Array.from(
			container.querySelectorAll<HTMLElement>('[data-active-filter-card]'),
		);
		if (cards.length === 0) return { index: 0, top: 0 };
		const containerRect = container.getBoundingClientRect();
		for (let i = 0; i < cards.length; i++) {
			const rect = cards[i].getBoundingClientRect();
			const midY = rect.top + rect.height / 2;
			if (clientY < midY) {
				return { index: i, top: rect.top - containerRect.top };
			}
		}
		const last = cards[cards.length - 1].getBoundingClientRect();
		return {
			index: cards.length,
			top: last.bottom - containerRect.top,
		};
	};

	return (
		<div
			ref={containerRef}
			className="relative"
			onDragOver={(e) => {
				if (disabled) return;
				// Only react to drags carrying a filter id (set by AvailableFilterItem).
				// Items dragged inside dnd-kit don't set HTML5 dataTransfer, so this
				// won't fire for internal reorders.
				const hasText = Array.from(e.dataTransfer.types).includes('text/plain');
				if (!hasText) return;
				e.preventDefault();
				e.dataTransfer.dropEffect = 'copy';
				const next = computeInsertion(e.clientY);
				if (next != null) {
					setHoverIndex(next.index);
					setIndicatorTop(next.top);
				}
			}}
			onDragLeave={(e) => {
				// Only clear when the pointer actually leaves the container — dragLeave
				// also fires when it moves over a child.
				if (containerRef.current == null) return;
				const related = e.relatedTarget as Node | null;
				if (related != null && containerRef.current.contains(related)) return;
				setHoverIndex(null);
				setIndicatorTop(null);
			}}
			onDrop={(e) => {
				if (disabled) return;
				e.preventDefault();
				const filterId = e.dataTransfer.getData('text/plain');
				setHoverIndex(null);
				setIndicatorTop(null);
				if (filterId.length === 0) return;
				const next = computeInsertion(e.clientY);
				if (next == null) return;
				onPositionalDrop(filterId, next.index);
			}}
		>
			{children}
			{hoverIndex != null && indicatorTop != null && (
				<div
					className={cn(
						'absolute left-0 right-0 h-0.5 pointer-events-none rounded',
						colors.classes.bgInfo,
					)}
					style={{ top: `${String(indicatorTop)}px` }}
				/>
			)}
		</div>
	);
}

interface EmptyActiveFiltersDropZoneProps {
	onDrop: (filterId: string) => void;
	disabled: boolean;
}

/**
 * Drop target rendered when the active filters list is empty — gives the user
 * a visible target to drag the first available filter onto. Uses native HTML5
 * drag events because there's no DraggableList wrapping the empty state.
 */
function EmptyActiveFiltersDropZone({
	onDrop,
	disabled,
}: EmptyActiveFiltersDropZoneProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [isDragOver, setIsDragOver] = useState(false);

	return (
		<div
			onDragOver={(e) => {
				if (disabled) return;
				e.preventDefault();
				setIsDragOver(true);
			}}
			onDragLeave={() => {
				setIsDragOver(false);
			}}
			onDrop={(e) => {
				e.preventDefault();
				setIsDragOver(false);
				if (disabled) return;
				const filterId = e.dataTransfer.getData('text/plain');
				if (filterId.length > 0) {
					onDrop(filterId);
				}
			}}
			className={cn(
				'rounded border-2 border-dashed py-6 text-center text-xs',
				isDragOver
					? cn(colors.classes.borderInfo, colors.classes.bgSecondary)
					: colors.classes.borderDefault,
				colors.classes.textMuted,
			)}
		>
			{t('Drag a filter from the Available list below to add it here.')}
		</div>
	);
}
